import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useRef,
	useState,
} from "react";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

interface TurnstileContextValue {
	/**
	 * The current Turnstile token, or null if not yet verified.
	 */
	token: string | null;
	/**
	 * Whether the Turnstile widget is ready (has generated a token).
	 */
	isReady: boolean;
	/**
	 * Reset the Turnstile widget to get a fresh token.
	 */
	reset: () => void;
	/**
	 * Get headers with the Turnstile token for API requests.
	 * Returns the current token from ref to avoid stale closures.
	 */
	getCaptchaHeaders: () => Record<string, string>;
}

const TurnstileContext = createContext<TurnstileContextValue | null>(null);

interface TurnstileProviderProps {
	children: ReactNode;
}

/**
 * Provider component that renders an invisible Turnstile widget
 * and provides the token via context.
 */
export function TurnstileProvider({ children }: TurnstileProviderProps) {
	const [token, setToken] = useState<string | null>(null);
	const tokenRef = useRef<string | null>(null);
	const turnstileRef = useRef<TurnstileInstance | null>(null);

	const reset = useCallback(() => {
		setToken(null);
		tokenRef.current = null;
		turnstileRef.current?.reset();
	}, []);

	// Use ref to avoid stale closure issues when called from form handlers
	const getCaptchaHeaders = useCallback((): Record<string, string> => {
		const currentToken = tokenRef.current;
		if (!currentToken) {
			console.warn("Turnstile token not ready yet");
			return {};
		}
		return { "x-captcha-response": currentToken };
	}, []);

	const handleSuccess = useCallback((newToken: string) => {
		tokenRef.current = newToken;
		setToken(newToken);
	}, []);

	const handleExpire = useCallback(() => {
		tokenRef.current = null;
		setToken(null);
		turnstileRef.current?.reset();
	}, []);

	const handleError = useCallback(() => {
		tokenRef.current = null;
		setToken(null);
		console.error("Turnstile verification failed");
	}, []);

	// If no site key is configured, provide a no-op implementation
	// that doesn't require captcha (for development without keys)
	if (!TURNSTILE_SITE_KEY) {
		return (
			<TurnstileContext.Provider
				value={{
					token: "disabled",
					isReady: true,
					reset: () => {},
					getCaptchaHeaders: () => ({}),
				}}
			>
				{children}
			</TurnstileContext.Provider>
		);
	}

	return (
		<TurnstileContext.Provider
			value={{
				token,
				isReady: !!token,
				reset,
				getCaptchaHeaders,
			}}
		>
			{children}
			<Turnstile
				ref={turnstileRef}
				siteKey={TURNSTILE_SITE_KEY}
				options={{
					size: "invisible",
				}}
				onSuccess={handleSuccess}
				onExpire={handleExpire}
				onError={handleError}
			/>
		</TurnstileContext.Provider>
	);
}

/**
 * Hook to access the Turnstile context.
 * Must be used within a TurnstileProvider.
 */
export function useTurnstile(): TurnstileContextValue {
	const context = useContext(TurnstileContext);
	if (!context) {
		throw new Error("useTurnstile must be used within a TurnstileProvider");
	}
	return context;
}
